'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CONSENT_GRANTED_EVENT, ConsentCategory } from '@framework/cookie-consent/constants';
import { getContainer } from '@framework/di/getContainer';
import TrackingConsent from '@framework/cookie-consent/TrackingConsent';
import hydrateConsentFromUrl from '@framework/cookie-consent/hydrateConsentFromUrl';

const useCookieBanner = () => {
  const container = getContainer();
  const trackingConsent = container.get(TrackingConsent);

  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isManageCookiesOpen, setIsManageCookiesOpen] = useState(false);
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(false);

  const closeBanner = useCallback(() => {
    setIsBannerVisible(false);
  }, []);

  const saveSettings = useCallback(() => {
    const categories = isMarketingAccepted
      ? [ConsentCategory.Functional, ConsentCategory.Marketing]
      : [ConsentCategory.Functional];
    trackingConsent.grantConsent(categories);
  }, [isMarketingAccepted]);

  const saveAll = useCallback(() => {
    trackingConsent.grantConsent();
  }, []);

  const openCookiesSettings = () => {
    setIsManageCookiesOpen(true);
  };

  const onMarketingChange = () => {
    setIsMarketingAccepted((prevState) => !prevState);
  };

  useEffect(() => {
    hydrateConsentFromUrl();

    if (!trackingConsent.hasConsent(ConsentCategory.Functional)) {
      setIsBannerVisible(true);
    }

    if (trackingConsent.hasConsent(ConsentCategory.Marketing)) {
      setIsMarketingAccepted(true);
    }

    const handleConsentGranted = () => {
      setIsBannerVisible(false);
    };

    window.addEventListener(CONSENT_GRANTED_EVENT, handleConsentGranted);
    return () => {
      window.removeEventListener(CONSENT_GRANTED_EVENT, handleConsentGranted);
    };
  }, []);

  return {
    isMarketingAccepted,
    isManageCookiesOpen,
    isBannerVisible,
    onMarketingChange,
    openCookiesSettings,
    closeBanner,
    saveSettings,
    saveAll,
  };
};

// @ts-ignore
const CookiesBannerContext = createContext<ReturnType<typeof useCookieBanner>>(null);

export const useCookiesBannerContext = () => {
  const context = useContext(CookiesBannerContext);
  if (!context) {
    throw new Error('useCookiesBannerContext must be used within the CookiesBannerContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const CookiesBannerContextProvider = ({ children }: IProps) => (
  <CookiesBannerContext.Provider value={ useCookieBanner() }>
    { children }
  </CookiesBannerContext.Provider>
);

export default CookiesBannerContextProvider;
